import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import {
  createDraftRegulation,
  fetchDraftRegulationById,
  updateDraftRegulation,
} from './Draft.services.tsx';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import i18n from '../../utils/i18n.ts';
import type { ApiError } from '../../types/App.ts';

export const useService = ({ regulationId }: { regulationId: number | null }) => {
  const { accounts } = useMsal();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const hasAccount = accounts.length > 0;

  const createMutation = useMutation({
    mutationKey: ['saveDraftRegulation'],
    mutationFn: createDraftRegulation,
    onSuccess: (id: number) => {
      toast.success(i18n.t('success.api.post-item', { item: i18n.t('module.draft') }));
      navigate(`/regulation/draft/view/${id}`);
      queryClient.invalidateQueries({ queryKey: ['draftRegulations'] });
      queryClient.invalidateQueries({ queryKey: ['draftRegulation'] });
    },
    onError: (error: ApiError) => {
      toast.error(error?.response?.data?.message || error.message);
    },
  });

  const updateMutation = useMutation({
    mutationKey: ['saveDraftRegulation'],
    mutationFn: updateDraftRegulation,
    onSuccess: (id: number) => {
      toast.success(i18n.t('success.api.put-item', { item: i18n.t('module.draft') }));
      navigate(`/regulation/draft/view/${id}`);
      queryClient.invalidateQueries({ queryKey: ['draftRegulations'] });
      queryClient.invalidateQueries({ queryKey: ['draftRegulation'] });
    },
    onError: (error: ApiError) => {
      toast.error(error?.response?.data?.message || error.message);
    },
  });

  const { data: regulation } = useQuery({
    queryKey: ['draftRegulation', regulationId],
    queryFn: () => fetchDraftRegulationById(regulationId),
    enabled: !!regulationId && hasAccount,
    retry: false,
  });

  return { createMutation, updateMutation, regulation };
};